import { useEffect, useMemo, useState } from 'react';
import { api } from '../../shell/bridge';
import { useChores } from './Chores/hooks';
import { assigneeLabel } from './Chores/helpers';
import { WeekBody } from './Chores/week';
import type { ChoreItem } from './Chores/types';
import type { DateView } from '../../../../shared/types/dates';

/**
 * Life → Week — the calendar table, promoted out of the Household screen into
 * its own sub-tab. One table over everything dated in the next seven days:
 * household items (the chore week rows) and locked/suggested dates, with a
 * Kind filter beside the existing day / responsible / complete filters.
 * Rows are shaped onto the ChoreItem slice the week body already reads, so
 * the filter logic (day key, assignee label) is shared, not duplicated —
 * a date carries no assignee, so it reads "Anyone" and always shows under
 * Everyone.
 */

type WeekRow = ChoreItem & { kind: 'Household' | 'Date' };

const KINDS: WeekRow['kind'][] = ['Household', 'Date'];

function dateRow(d: DateView): WeekRow {
  return {
    choreId: 'date-' + d.ideaId,
    title: d.title,
    cadence: 'once',
    freq: null,
    recurMode: 'fixed',
    recurEveryDays: null,
    assigneeId: null,
    assignMode: 'fixed',
    dueAt: d.when,
    status: 'open',
    completedAt: null,
    completedBy: null,
    createdBy: '',
    createdAt: 0,
    updatedAt: 0,
    syncGoogle: false,
    snoozeCount: 0,
    reactions: [],
    doneNote: null,
    doneNoteBy: null,
    kind: 'Date',
  };
}

export function Week() {
  const h = useChores();
  const [dates, setDates] = useState<DateView[] | null>(null);
  const [kind, setKind] = useState('all');

  useEffect(() => {
    let live = true;
    api<{ ideas: DateView[] }>('/api/dates')
      .then(d => { if (live) setDates(d.ideas ?? []); })
      .catch(() => { if (live) setDates([]); });
    return () => { live = false; };
  }, []);

  const rows: WeekRow[] = useMemo(() => {
    const chores: WeekRow[] = (h.weekRows ?? []).map(c => ({ ...c, kind: 'Household' }));
    const dateRows: WeekRow[] = (dates ?? [])
      .filter(d => d.when != null && (d.state === 'suggested' || d.state === 'locked'))
      .map(dateRow);
    return [...chores, ...dateRows].sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0));
  }, [h.weekRows, dates]);

  const dayKey = h.weekDayKey;
  const days = useMemo(() => [...new Set(rows.map(dayKey))], [rows, dayKey]);
  const filtered = useMemo(
    () =>
      rows.filter(c => {
        if (h.weekDay !== 'all' && dayKey(c) !== h.weekDay) return false;
        if (h.weekWho !== 'all' && assigneeLabel(c, h.me) !== h.weekWho) return false;
        if (h.weekDone !== 'all' && c.status !== h.weekDone) return false;
        if (kind !== 'all' && c.kind !== kind) return false;
        return true;
      }),
    [rows, h.weekDay, h.weekWho, h.weekDone, h.me, dayKey, kind],
  );

  return (
    <section className="ch-panel" id="life-week-panel" aria-labelledby="life-week-title">
      <h2
        id="life-week-title"
        className="ch-title"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--type-h3-size)',
          letterSpacing: 'var(--type-tracking-wider)',
          fontWeight: 400,
          color: 'var(--color-cream)',
        }}
      >
        Week
      </h2>
      <WeekBody
        weekDay={h.weekDay} setWeekDay={h.setWeekDay}
        weekWho={h.weekWho} setWeekWho={h.setWeekWho}
        weekDone={h.weekDone} setWeekDone={h.setWeekDone}
        weekDays={days} weekFiltered={filtered} weekDayKey={h.weekDayKey} me={h.me}
        kind={kind} setKind={setKind} kinds={KINDS} kindOf={c => (c as WeekRow).kind}
      />
    </section>
  );
}
